// src/pages/Admin/Components/ProductsPage.jsx
import React, { useState, useRef, useEffect } from 'react';
import {
  Package,
  Search,
  Plus,
  X,
  Loader2,
  MoreVertical,
  ShoppingBag,
  ChevronRight,
  ChevronLeft,
  Edit,
  Trash2,
  Tag
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProducts } from '../../../redux/features/Product/productThunk';
import { adminDeleteProduct } from '../../../services/adminServices';
import { useNavigate } from 'react-router-dom';

const ProductsPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { userInfo } = useSelector((state) => state.auth) || {};
  const { products: apiProducts, loading: apiLoading, error: apiError } = useSelector((state) => state.listProduct);

  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRefs = useRef({});

  const products = (apiProducts || []).map(p => ({
    id: p._id,
    name: p.name,
    category: p.category || 'دسته‌بندی نشده',
    price: p.price || 0,
    stock: p.countInStock || 0,
    sales: p.numReviews || 0,
    image: p.image || '',
  }));

  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const itemsPerPage = 9;
  const searchTimeoutRef = useRef(null);
  const searchDelayTimeoutRef = useRef(null);

  useEffect(() => {
    dispatch(fetchProducts());
  }, [dispatch]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId !== null) {
        const menuElement = menuRefs.current[openMenuId];
        if (menuElement && !menuElement.contains(event.target)) {
          setOpenMenuId(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  const filteredProducts = products.filter(product => {
    if (activeTab === 'all') return true;
    if (activeTab === 'active') return product.stock > 5;
    if (activeTab === 'low') return product.stock > 0 && product.stock <= 5;
    if (activeTab === 'out') return product.stock === 0;
    return true;
  });

  const handleSearch = (value) => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchDelayTimeoutRef.current) clearTimeout(searchDelayTimeoutRef.current);
    setSearchTerm(value);
    if (!value.trim()) {
      setSearchResults([]);
      setIsSearching(false);
      setCurrentPage(1);
      return;
    }
    searchTimeoutRef.current = setTimeout(() => {
      setIsSearching(true);
      searchDelayTimeoutRef.current = setTimeout(() => {
        const results = filteredProducts.filter(p =>
          p.name.includes(value.trim()) ||
          p.category.includes(value.trim())
        );
        setSearchResults(results);
        setIsSearching(false);
        setCurrentPage(1);
      }, 500);
    }, 300);
  };

  const clearSearch = () => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (searchDelayTimeoutRef.current) clearTimeout(searchDelayTimeoutRef.current);
    setSearchTerm('');
    setSearchResults([]);
    setIsSearching(false);
    setCurrentPage(1);
  };

  const currentData = searchTerm.trim() ? searchResults : filteredProducts;
  const totalItems = currentData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const currentItems = currentData.slice(startIndex, endIndex);

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  const getPaginationButtons = () => {
    const buttons = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) buttons.push(i);
    } else {
      buttons.push(1);
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      if (currentPage <= 2) end = 4;
      if (currentPage >= totalPages - 1) start = totalPages - 3;
      if (start > 2) buttons.push('...');
      for (let i = start; i <= end; i++) buttons.push(i);
      if (end < totalPages - 1) buttons.push('...');
      buttons.push(totalPages);
    }
    return buttons;
  };

  const handleDelete = (productId) => {
    if (window.confirm('آیا از حذف این محصول مطمئن هستید؟')) {
      adminDeleteProduct(productId, { userInfo, dispatch })
        .then(() => {
          dispatch(fetchProducts());
          setOpenMenuId(null);
        })
        .catch(err => {
          alert('خطا در حذف محصول');
          console.error(err);
        });
    }
  };

  const handleEdit = (productId) => {
    navigate(`/admin/product/edit/${productId}`);
    setOpenMenuId(null);
  };

  const formatPrice = (price) => {
    return Math.round(price).toLocaleString('fa-IR');
  };

  const SkeletonCard = () => (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden animate-pulse">
      <div className="h-48 bg-gray-200 w-full"></div>
      <div className="p-4 space-y-3">
        <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        <div className="h-5 bg-gray-200 rounded w-3/4"></div>
        <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        <div className="flex justify-between pt-3 border-t border-gray-100">
          <div className="h-4 bg-gray-200 rounded w-1/3"></div>
          <div className="h-4 bg-gray-200 rounded w-1/4"></div>
        </div>
      </div>
    </div>
  );

  if (apiLoading) {
    return (
      <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="text-center mb-8 animate-pulse">
          <div className="h-8 w-32 bg-gray-200 rounded mx-auto"></div>
          <div className="h-4 w-48 bg-gray-200 rounded mx-auto mt-2"></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => <SkeletonCard key={i} />)}
        </div>
      </div>
    );
  }

  if (apiError) {
    return (
      <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium">خطا: {apiError}</p>
          <button onClick={() => dispatch(fetchProducts())} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
            تلاش مجدد
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-w-0 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen" dir="rtl">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight mb-2">محصولات</h1>
        <p className="text-sm text-gray-500">محصولات فروشگاه، موجودی و قیمت‌ها را مدیریت کنید.</p>
      </div>

      <div className="bg-white p-4 md:p-6 rounded-xl shadow-sm border border-gray-200 mb-8 flex flex-col md:flex-row gap-4 items-center justify-between">
        <div className="flex rounded-sm p-2 flex-wrap gap-2 w-full md:w-auto justify-center md:justify-start">
          {[
            { id: 'all', label: 'همه محصولات' },
            { id: 'active', label: 'موجود' },
            { id: 'low', label: 'رو به اتمام' },
            { id: 'out', label: 'ناموجود' },
          ].map((filter) => (
            <button
              key={filter.id}
              onClick={() => { setActiveTab(filter.id); setCurrentPage(1); clearSearch(); }}
              className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                activeTab === filter.id
                  ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-72">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
            {isSearching ? <Loader2 className="w-5 h-5 text-blue-500 animate-spin" /> : <Search className="w-5 h-5 text-gray-400" />}
          </div>
          <input 
            type="text" 
            placeholder="جستجو بر اساس نام یا دسته بندی..." 
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 pr-10 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            dir="rtl"
          />
          {searchTerm && (
            <button onClick={clearSearch} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {searchTerm.trim() && !isSearching && searchResults.length > 0 && (
        <div className="mb-4 text-sm text-gray-500">
          <span className="font-medium text-blue-600">{searchResults.length}</span>
          <span> نتیجه برای "</span>
          <span className="font-medium">{searchTerm}</span>
          <span>" پیدا شد</span>
        </div>
      )}
      {searchTerm.trim() && !isSearching && searchResults.length === 0 && (
        <div className="mb-4 text-sm text-gray-500">
          <span>نتیجه‌ای برای "</span>
          <span className="font-medium">{searchTerm}</span>
          <span>" پیدا نشد</span>
        </div>
      )}

      {isSearching ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {Array(6).fill(0).map((_, i) => <SkeletonCard key={i} />)}
        </div>
      ) : currentItems.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {currentItems.map((product) => (
            <div key={product.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-visible hover:shadow-md transition-shadow duration-300 group flex flex-col h-full relative">
              <div className="relative h-48 bg-gray-100 overflow-hidden flex-shrink-0 rounded-t-xl">
                <img 
                  src={product.image || 'https://via.placeholder.com/400x300?text=No+Image'} 
                  alt={product.name} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=No+Image';
                  }}
                />
                <div className="absolute top-3 left-3 z-20" ref={(el) => menuRefs.current[product.id] = el}>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenMenuId(openMenuId === product.id ? null : product.id);
                    }}
                    className="bg-white/90 p-1.5 rounded-full shadow-sm hover:bg-white transition-colors"
                  >
                    <MoreVertical className="w-4 h-4 text-gray-600" />
                  </button>
                  {openMenuId === product.id && (
                    <div className="absolute left-0 mt-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999] min-w-[140px]">
                      <button
                        onClick={() => handleEdit(product.id)}
                        className="w-full text-right px-4 py-2.5 text-sm text-gray-700 hover:bg-blue-50 flex items-center gap-2 transition-colors"
                      >
                        <Edit className="w-4 h-4 text-blue-600" />
                        ویرایش
                      </button>
                      <button
                        onClick={() => handleDelete(product.id)}
                        className="w-full text-right px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 transition-colors border-t border-gray-100"
                      >
                        <Trash2 className="w-4 h-4" />
                        حذف
                      </button>
                    </div>
                  )}
                </div>
              </div>
              <div className="p-3.5 text-right flex flex-col flex-1">
                <div className="flex items-center gap-1.5 mb-1.5">
                  <Tag className="w-3.5 h-3.5 text-blue-500" />
                  <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2.5 py-0.5 rounded-full">
                    {product.category}
                  </span>
                </div>
                <h3 className="font-bold text-gray-800 text-base mb-2 line-clamp-2">
                  {product.name}
                </h3>
                <p className="text-[#c89f5e] font-bold text-lg mb-3 tracking-tight">
                  {formatPrice(product.price)} 
                  <span className="text-xs font-normal text-gray-400 mr-1">تومان</span>
                </p>
                <div className="flex justify-between items-center text-xs text-gray-500 pt-3 border-t border-gray-100 mt-auto">
                  <span className="flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-full">
                    <ShoppingBag className="w-3.5 h-3.5 text-gray-400" />
                    {product.sales} فروش
                  </span>
                  <span className={`flex items-center gap-1.5 bg-gray-50 px-2.5 py-1 rounded-full ${
                    product.stock > 5 ? 'text-green-600' :
                    product.stock > 0 && product.stock <= 5 ? 'text-yellow-600' : 'text-red-600'
                  }`}>
                    <div className={`w-1.5 h-1.5 rounded-full ${
                      product.stock > 5 ? 'bg-green-500' :
                      product.stock > 0 && product.stock <= 5 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}></div>
                    {product.stock} عدد
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500 font-medium bg-white rounded-xl shadow-sm border border-gray-100">
          <Package className="w-12 h-12 mx-auto text-gray-300 mb-2" />
          محصولی با این مشخصات یافت نشد.
        </div>
      )}

      {!isSearching && totalItems > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-4 bg-white rounded-xl shadow-sm border border-gray-100 gap-4">
          <div className="text-sm text-gray-600 font-medium order-2 sm:order-1">
            نمایش <span className="font-bold text-gray-900">{startIndex + 1}</span> تا <span className="font-bold text-gray-900">{endIndex}</span> عدد از <span className="font-bold text-gray-900">{totalItems}</span> محصول
          </div>
          <div className="flex items-center gap-2 order-1 sm:order-2">
            <button 
              onClick={() => goToPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50 transition-colors shadow-sm"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            
            {getPaginationButtons().map((btn, index) => (
              <button 
                key={index}
                onClick={() => typeof btn === 'number' && goToPage(btn)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-bold transition-colors shadow-sm ${
                  btn === currentPage
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-200'
                    : btn === '...'
                    ? 'bg-transparent text-gray-400 cursor-default'
                    : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
                disabled={btn === '...'}
              >
                {btn}
              </button>
            ))}
            
            <button 
              onClick={() => goToPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-800 disabled:opacity-50 transition-colors shadow-sm"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductsPage;